import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { getOrganization, updateOrganization } from '@/services/api/organizations';
import { OrganizationProfileUpdate } from '@/domains';
import { imageSchema } from '@/validation';

import If from '@/components/If';
import LoadData from '@/components/LoadData';
import Loading from '@/components/Loading';
import Placeholder from '@/components/Placeholder';
import { ImageInputWithValidation } from '@/components/Input/ImageInput';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const formSchema = z.object({
  image: imageSchema,
  name: z.string().min(1, 'Organization name is required'),
  description: z.string().min(1, 'Description is required'),
  address: z.string().min(1, 'Address is required')
});

type FormValues = z.infer<typeof formSchema>;

interface OrganizationFormProps {
  organization: any;
  onSubmit: (values: FormValues) => Promise<void>;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({ organization, onSubmit }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: organization.name,
      description: organization.description,
      address: organization.address,
      image: { url: organization.imageUrl, thumbnailUrl: organization.thumbnailUrl }
    }
  });

  return (
    <Card>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cover Image</FormLabel>
                  <FormControl>
                    <ImageInputWithValidation
                      onImageUploaded={(image) => {
                        form.setValue('image', image);
                      }}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organization Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter organization name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Business description" className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Set business address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={form.formState.isSubmitting || !form.formState.isDirty}>
              {form.formState.isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export interface Props {
  id: string;
}

const OrganizationGeneralTab: React.FC<Props> = ({ id }) => {
  return (
    <React.Fragment>
      <LoadData id={id} load={getOrganization}>
        {({ value: organization, loading }) => (
          <If
            condition={loading}
            then={() => <Loading visible={loading} />}
            else={() => (
              <If
                condition={!organization}
                then={() => (
                  <Placeholder
                    className="h-full justify-center"
                    image="bell"
                    size="xs"
                    title="Oups"
                    description="Organization not found. Please try again later."
                  />
                )}
                else={() => {
                  async function handleSubmit(values: FormValues) {
                    try {
                      await updateOrganization(id, values);
                    } catch (error) {
                      console.error('Failed to update organization:', error);
                    }
                  }

                  return <OrganizationForm organization={organization} onSubmit={handleSubmit} />;
                }}
              />
            )}
          />
        )}
      </LoadData>
    </React.Fragment>
  );
};

export default OrganizationGeneralTab;
